import { useState, useEffect, useRef, useImperativeHandle, forwardRef, useCallback } from "react";
import type { Chat, EnrichedMessage } from "@/frontend/lib/types";
import { transformMessages } from "@/frontend/lib/message-transformer";
import { MessageBubble } from "./MessageBubble";
import { Input } from "@/frontend/components/ui/input";
import { Button } from "@/frontend/components/ui/button";
import { Search, Copy, Check } from "lucide-react";
import { useVirtualizer } from "@tanstack/react-virtual";

interface ChatViewProps {
  chat: Chat;
  messages: EnrichedMessage[];
  activeRightTab?: "incidents" | "askAi";
  onRightTabChange?: (tab: "incidents" | "askAi") => void;
}

export interface ChatViewRef {
  scrollToDate: (date: string) => void;
}

export const ChatView = forwardRef<ChatViewRef, ChatViewProps>(({ chat, messages, activeRightTab = "incidents", onRightTabChange }, ref) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [isCopied, setIsCopied] = useState(false);
  const [currentDate, setCurrentDate] = useState<string>("");
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [overscan, setOverscan] = useState(10);

  const estimateMessageSize = useCallback((index: number): number => {
    const message = messages[index];
    if (!message) return 100;

    let estimatedHeight = 0;

    if (message.showDateSeparator) {
      estimatedHeight += 60;
    }

    if (message.type === "service") {
      return estimatedHeight + 40;
    }

    const isFirstInGroup = index === 0 ||
      messages[index - 1]?.fromTelegramId !== message.fromTelegramId ||
      message.showDateSeparator;

    if (isFirstInGroup) {
      estimatedHeight += 35;
    }

    estimatedHeight += 20;

    if (message.voiceUrl) {
      estimatedHeight += 80;
    }

    if (message.photoUrl) {
      estimatedHeight += 270;
    }

    if (message.videoUrl || message.videoNoteUrl) {
      estimatedHeight += 100;
    }

    if (message.fileUrl) {
      estimatedHeight += 50;
    }

    if (message.replyTo) {
      estimatedHeight += 45;
    }

    if (message.content) {
      const textLength = message.content.replace(/<[^>]*>/g, '').length;
      const lines = Math.ceil(textLength / 50);
      estimatedHeight += Math.min(lines * 22, 400);
    }

    if (message.reactions && message.reactions.length > 0) {
      estimatedHeight += 35;
    }

    return Math.max(estimatedHeight, 60);
  }, [messages]);

  const virtualizer = useVirtualizer({
    count: messages.length,
    getScrollElement: () => messagesContainerRef.current,
    estimateSize: estimateMessageSize,
    overscan,
  });

  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(() => {
        virtualizer.scrollToIndex(messages.length - 1, { align: "end" });
      }, 0);
    }
  }, [messages.length]);

  const formatDateForSticky = (date: Date): string => {
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const updateCurrentDate = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container || messages.length === 0) return;

    setIsScrolling(true);

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 1000);

    const containerRect = container.getBoundingClientRect();
    const topThreshold = containerRect.top + 60;

    for (const message of messages) {
      const element = document.getElementById(`message-${message.id}`);
      if (element) {
        const rect = element.getBoundingClientRect();
        if (rect.top <= topThreshold && rect.bottom >= containerRect.top) {
          const date = new Date(message.createdAt);
          setCurrentDate(formatDateForSticky(date));
          return;
        }
      }
    }
  }, [messages]);

  useEffect(() => {
    const timer = setTimeout(() => {
      updateCurrentDate();
    }, 100);
    return () => clearTimeout(timer);
  }, [updateCurrentDate]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    container.addEventListener("scroll", updateCurrentDate);
    return () => {
      container.removeEventListener("scroll", updateCurrentDate);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [updateCurrentDate]);

  const scrollToMessage = (telegramMessageId: number) => {
    const messageIndex = messages.findIndex((m) => m.telegramMessageId === telegramMessageId);
    if (messageIndex !== -1) {
      const currentRange = virtualizer.range;
      const currentIndex = currentRange ? Math.floor((currentRange.startIndex + currentRange.endIndex) / 2) : 0;
      const distance = Math.abs(messageIndex - currentIndex);

      if (distance > 100) {
        setOverscan(50);
      }

      virtualizer.scrollToIndex(messageIndex, { align: "center", behavior: "smooth" });

      setTimeout(() => {
        virtualizer.scrollToIndex(messageIndex, { align: "center" });

        setTimeout(() => {
          const element = document.getElementById(`message-${messages[messageIndex]!.id}`);
          if (element) {
            const container = messagesContainerRef.current;
            if (container) {
              const containerRect = container.getBoundingClientRect();
              const elementRect = element.getBoundingClientRect();
              const isInView = elementRect.top >= containerRect.top && elementRect.bottom <= containerRect.bottom;

              if (!isInView) {
                element.scrollIntoView({ behavior: "smooth", block: "center" });
              }
            }

            element.classList.add("message-highlight");
            setTimeout(() => element.classList.remove("message-highlight"), 800);
          }

          if (distance > 100) {
            setTimeout(() => setOverscan(10), 1000);
          }
        }, 200);
      }, 150);
    }
  };

  const scrollToDate = (date: string) => {
    if (messages.length === 0) {
      return;
    }

    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0);

    let closestMessageIndex = 0;
    let minDiff = Math.abs(new Date(messages[0]!.createdAt).getTime() - targetDate.getTime());

    for (let i = 0; i < messages.length; i++) {
      const msg = messages[i]!;
      const messageDate = new Date(msg.createdAt);
      messageDate.setHours(0, 0, 0, 0);
      const diff = Math.abs(messageDate.getTime() - targetDate.getTime());

      if (diff < minDiff) {
        minDiff = diff;
        closestMessageIndex = i;
      }

      if (diff === 0) {
        break;
      }
    }

    const currentRange = virtualizer.range;
    const currentIndex = currentRange ? Math.floor((currentRange.startIndex + currentRange.endIndex) / 2) : 0;
    const distance = Math.abs(closestMessageIndex - currentIndex);

    if (distance > 100) {
      setOverscan(50);
    }

    virtualizer.scrollToIndex(closestMessageIndex, { align: "center", behavior: "smooth" });

    setTimeout(() => {
      virtualizer.scrollToIndex(closestMessageIndex, { align: "center" });

      setTimeout(() => {
        const element = document.getElementById(`message-${messages[closestMessageIndex]!.id}`);
        if (element) {
          const container = messagesContainerRef.current;
          if (container) {
            const containerRect = container.getBoundingClientRect();
            const elementRect = element.getBoundingClientRect();
            const isInView = elementRect.top >= containerRect.top && elementRect.bottom <= containerRect.bottom;

            if (!isInView) {
              element.scrollIntoView({ behavior: "smooth", block: "center" });
            }
          }

          element.classList.add("message-highlight");
          setTimeout(() => element.classList.remove("message-highlight"), 800);
        }

        if (distance > 100) {
          setTimeout(() => setOverscan(10), 1000);
        }
      }, 200);
    }, 150);
  };

  useImperativeHandle(ref, () => ({
    scrollToDate,
  }));

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(chat.name);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 1500);
    } catch (err) {
      // Silent fail
    }
  };

  return (
    <div className="flex h-full flex-col bg-background">
      <div className="border-b border-border bg-card p-4">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold text-foreground">{chat.name}</h1>
          <button
            onClick={copyToClipboard}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Копировать название чата"
          >
            {isCopied ? (
              <Check className="h-4 w-4 text-green-500" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </button>
        </div>
        {chat.clientName && <p className="text-sm text-muted-foreground">{chat.clientName}</p>}
      </div>

      <div className="flex items-center gap-2 border-b border-border bg-card p-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search messages..." className="pl-9" />
        </div>
        <Button
          variant={activeRightTab === "incidents" ? "default" : "ghost"}
          size="sm"
          onClick={() => onRightTabChange?.("incidents")}
        >
          Incidents
        </Button>
        <Button
          variant={activeRightTab === "askAi" ? "default" : "ghost"}
          size="sm"
          onClick={() => onRightTabChange?.("askAi")}
        >
          Ask AI
        </Button>
      </div>

      <div ref={messagesContainerRef} className="relative flex-1 overflow-y-auto p-4">
        {currentDate && (
          <div className="sticky top-0 z-50 flex justify-center py-2 pointer-events-none">
            <span
              className={`rounded-full bg-primary/90 px-4 py-1.5 text-xs font-medium text-primary-foreground shadow-lg backdrop-blur-sm transition-opacity duration-300 ${
                isScrolling ? 'opacity-100' : 'opacity-0'
              }`}
            >
              {currentDate}
            </span>
          </div>
        )}
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualizer.getVirtualItems().map((virtualItem) => {
            const message = messages[virtualItem.index]!;
            return (
              <div
                key={virtualItem.key}
                data-index={virtualItem.index}
                ref={virtualizer.measureElement}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualItem.start}px)`,
                }}
              >
                {message.showDateSeparator && (
                  <div className="flex justify-center py-4">
                    <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
                      {message.dateSeparatorText}
                    </span>
                  </div>
                )}

                <MessageBubble message={message} messages={messages} onReplyClick={scrollToMessage} />
              </div>
            );
          })}
        </div>
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
});
